import { type StudyMoeSelectDto, type StudyMosSelectDto } from '@@types/api.types';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import {
  useCreateMeiaMoeEntry,
  useCreateMeiaMosEntry,
  useDeleteMeiaMoeEntry,
  useDeleteMeiaMosEntry,
  useGetMeiaMoeTemplates,
  useGetMeiaMosTemplates,
  useUpdateMeiaMoeEntry,
  useUpdateMeiaMosEntry,
} from '@queries/meia.query';
import { type MeiaMosWithChildrenFormValues, meiaMosWithChildrenSchema } from '@schemas/meia.schema';
import { Trash2Icon } from 'lucide-react';
import { type FC, type ReactNode, useState } from 'react';
import type { ZodIssue } from 'zod';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';
import { buildEmptyMoe, moeFromServer, persistMoeTree } from './meia-measures.util';
import { MeiaMoeCardGroup } from './meia-moe-card.component';
import { MeiaTemplatePopover } from './meia-template-popover.component';

type MeiaMosFormChildren = (params: {
  /** Delete control for an existing MoS; `undefined` when adding a new one. */
  deleteButton: ReactNode;
  /** Reads the form's current dirty state — for host close-guards. */
  getIsDirty: () => boolean;
  /** Whether this form edits an existing MoS. */
  isEdit: boolean;
  isSaving: boolean;
  /** The MoS description + MoE field-array form, ready to drop into the host body. */
  mosForm: ReactNode;
  /** MoS template picker; place it next to the form title. */
  mosTemplatePopover: ReactNode;
  submit: () => void;
}) => ReactNode;

type MeiaMosFormProps = {
  children: MeiaMosFormChildren;
  missionId: string;
  /** Server snapshot of this MoS's MoE entries; empty for a new MoS. */
  moes: StudyMoeSelectDto[];
  mos?: StudyMosSelectDto;
  /** Called after a successful save or delete; hosts use it to close/dismiss. */
  onSaved: () => void;
};

/**
 * Measure-of-Success editing form (description + nested MoE field array with
 * advanced scoring) hosted by the standalone `MeiaMosModal` (mission + P1 measures).
 * The render-prop hands back the form body, submit handler, pending flag, delete
 * control, and template picker so the host owns the surrounding chrome.
 */
export const MeiaMosForm: FC<MeiaMosFormProps> = (props) => {
  const { children, missionId, moes, mos, onSaved } = props;
  const isEdit = Boolean(mos);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [erroredMoeLocalIds, setErroredMoeLocalIds] = useState<ReadonlySet<string>>(new Set());

  const { data: mosTemplates } = useGetMeiaMosTemplates();
  const { data: moeTemplates } = useGetMeiaMoeTemplates();
  const resolvedMosTemplates = mosTemplates?.mosTemplates ?? [];
  const resolvedMoeTemplates = moeTemplates?.moeTemplates ?? [];

  const { mutateAsync: createMos } = useCreateMeiaMosEntry();
  const { mutateAsync: updateMos } = useUpdateMeiaMosEntry();
  const { mutateAsync: deleteMos } = useDeleteMeiaMosEntry();
  const { mutateAsync: createMoe } = useCreateMeiaMoeEntry();
  const { mutateAsync: updateMoe } = useUpdateMeiaMoeEntry();
  const { mutateAsync: deleteMoe } = useDeleteMeiaMoeEntry();

  const defaultValues: MeiaMosWithChildrenFormValues = {
    description: mos?.description ?? '',
    moeEntries: moes.map((moe) => moeFromServer(moe)),
    mosTemplateCode: null,
    removedMoeIds: [],
  };

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      setErroredMoeLocalIds(new Set());

      setIsSaving(true);
      try {
        const mosId = mos?.id
          ? (await updateMos({ description: value.description, missionId, mosId: mos.id })).id
          : (await createMos({ description: value.description, missionId })).id;

        await persistMoeTree({
          createMoe,
          deleteMoe,
          moeEntries: value.moeEntries,
          mosId,
          onMoeCreated: ({ created, moeIndex }) => {
            form.setFieldValue(`moeEntries[${moeIndex}].id`, created.id);
          },
          removedMoeIds: value.removedMoeIds,
          updateMoe,
        });

        successToast({ description: 'Measure of Success saved.' });
        onSaved();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to save measure tree.';
        errorToast({ description: message });
      } finally {
        setIsSaving(false);
      }
    },
    onSubmitInvalid: ({ value }) => {
      const validation = meiaMosWithChildrenSchema.safeParse(value);
      if (!validation.success) {
        const erroredLocalIds = new Set<string>();
        for (const issue of validation.error.issues) {
          if (issue.path[0] !== 'moeEntries' || typeof issue.path[1] !== 'number') continue;
          const moe = value.moeEntries[issue.path[1]];
          if (moe) erroredLocalIds.add(moe._localId);
        }
        setErroredMoeLocalIds(erroredLocalIds);
        const weightIssue: ZodIssue | undefined = validation.error.issues.find(
          (issue) => issue.path[0] === 'moeEntries' && issue.path[2] === 'weight',
        );
        errorToast({
          description: weightIssue?.message ?? 'Please complete the required fields on every card.',
        });
        return;
      }
      errorToast({ description: 'Please complete the required fields on every card.' });
    },
    validators: { onSubmit: meiaMosWithChildrenSchema },
  });

  const handleConfirmDelete = async (): Promise<void> => {
    if (!mos) return;
    await deleteMos({ missionId, mosId: mos.id });
    successToast({ description: 'Measure of Success deleted.' });
    onSaved();
  };

  const submit = (): void => void form.handleSubmit();

  const deleteButton = isEdit ? (
    <Button
      onClick={() => setIsConfirmingDelete(true)}
      type="button"
      variant="destructive"
    >
      <Trash2Icon className="size-4" />
      Delete
    </Button>
  ) : undefined;

  const mosTemplatePopover = (
    <MeiaTemplatePopover
      onSelect={(template) => {
        form.setFieldValue('description', template.description ?? template.name);
        form.setFieldValue('mosTemplateCode', template.id);
      }}
      templates={resolvedMosTemplates.map((template) => ({
        description: template.description,
        id: template.id,
        name: template.name,
      }))}
    />
  );

  const mosForm = (
    <form
      {...MEIA_AUTOFILL_IGNORE_PROPS}
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        submit();
      }}
    >
      <form.AppForm>
        <form.AppField name="description">
          {(field) => (
            <field.TextAreaField
              label="Description"
              placeholder="Suppress integrated air defenses inside the first island chain — what does winning look like?"
              required
              rows={3}
            />
          )}
        </form.AppField>

        <div className="flex flex-col gap-2 pt-2">
          <form.AppField
            mode="array"
            name="moeEntries"
          >
            {(moeArrayField) => {
              const moeEntries = moeArrayField.state.value ?? [];
              return (
                <div className="flex flex-col gap-3">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Measures of Effectiveness ({moeEntries.length})
                  </h3>

                  {moeEntries.map((entry, moeIdx) => (
                    <MeiaMoeCardGroup
                      canEdit
                      fields={`moeEntries[${moeIdx}]`}
                      form={form}
                      hasError={erroredMoeLocalIds.has(entry._localId)}
                      key={entry._localId}
                      moeOrdinal={moeIdx + 1}
                      moeTemplates={resolvedMoeTemplates.map((template) => ({
                        description: template.description,
                        id: template.id,
                        name: template.name,
                      }))}
                      onRemove={() => {
                        if (entry.id) {
                          form.setFieldValue('removedMoeIds', (current) => [...current, entry.id ?? '']);
                        }
                        moeArrayField.removeValue(moeIdx);
                      }}
                    />
                  ))}

                  <Button
                    className="self-start"
                    onClick={() => moeArrayField.pushValue(buildEmptyMoe())}
                    type="button"
                    variant="link"
                  >
                    + Add Measure of Effectiveness
                  </Button>
                </div>
              );
            }}
          </form.AppField>
        </div>
      </form.AppForm>
    </form>
  );

  return (
    <>
      {children({
        deleteButton,
        getIsDirty: () => form.state.isDirty,
        isEdit,
        isSaving,
        mosForm,
        mosTemplatePopover,
        submit,
      })}
      {mos && (
        <ConfirmDeleteModal
          isOpen={isConfirmingDelete}
          itemName="measure of success"
          onConfirm={handleConfirmDelete}
          setIsOpen={setIsConfirmingDelete}
        />
      )}
    </>
  );
};
